/* eslint-disable max-len */
import {
  SimpleForm,
  required,
  ReferenceInput,
  AutocompleteInput,
  useTranslate,
  useNotify,
  TextInput
} from "react-admin";
import { useLocation } from "react-router-dom";
import * as User from "../providers/user";
import { Dispatch, SetStateAction, useRef, useState } from "react";
import { FIND_FIRST_USER_BY_PHONE, GET_ARTISTS, GET_LOCATIONS } from "../gql";
import { useLazyQuery, useQuery } from "@apollo/client";
import PhoneInput from "./PhoneInput";
import { CustomButton } from "./CustomButton";
import CheckUserByPhoneNumber from "./CheckUserByPhoneNumber";
import {
  Avatar,
  Box,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BookFromArtistCalendarFormHelper from "./BookFromArtistCalendarFormHelper";
import AddServicesAndAddOnsFromArtistCalendar from "./AddServicesAndAddOnsFromArtistCalendar";
import { Info } from "@mui/icons-material";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import "yup-phone";
import { isPhoneValid } from "utils/isPhoneValid";
import { FailedOrderIcon } from "assets/Icons";
import dayjs from "dayjs";
import * as Axios from "providers/axios";
import { getRawPhoneNumber } from "utils/getRawPhoneNumber";
import TimePickerInput from "./TimePickerInput";
import DatePickerInput from "./DatePickerInput";

interface BookFromArtistCalendarProps {
  artist: any;
  selectedDate?: string;
  selectedStart?: string;
  selectedEnd?: string;
  closeCreateDialogAndRefresh?: () => void;
  createDialogOpen: string | null;
  filteredLocationId?: string;
  closeCreateDialog: () => void;
  setBlockTimeDialogOpen: Dispatch<SetStateAction<boolean>>;
}

interface LocationResult {
  id: string;
  company_id: string;
  timezone: string;
}

interface RequestBody {
  start_time: string;
  location_id: string;
  services: RequestBodyService[];
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  customer_id?: string;
  notes?: string;
}

interface RequestBodyService {
  artist_id: string;
  service_id: string;
  addons_ids: string;
}

export const emptyBookAppointmentFull = {
  start_time: "",
  end_time: "",
  location: null,
  services: [],
  payment_method_id: "",
  bookingUnavailable: true
};

export default function BookFromArtistCalendar({
  artist,
  selectedStart,
  selectedEnd,
  closeCreateDialogAndRefresh,
  filteredLocationId,
  createDialogOpen,
  closeCreateDialog,
  setBlockTimeDialogOpen
}: BookFromArtistCalendarProps) {
  const { permissions } = User.useUser();
  const { api } = Axios.useAxios();
  const translate = useTranslate();
  const notify = useNotify();
  const location = useLocation();
  const [errors, setErrors] = useState<any>({});
  const [resetUserInputs, setResetUserInputs] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isCustomerSearchTriggered, setIsCustomerSearchTriggered] =
    useState(false);
  const [userSelectMode, setUserSelectMode] = useState<string | null>(
    "customers"
  );
  const userSelectModeRef = useRef(userSelectMode);
  userSelectModeRef.current = userSelectMode;
  const [existingUserPhone, setExistingUserPhone] = useState<{
    phone: string;
  } | null>(null);
  const [isCloseIconShaking, setIsCloseIconShaking] = useState(false);
  const [cart, setCart] = useState<any>(emptyBookAppointmentFull);
  const [isTooltipOpen, setIsTooltipOpen] = useState("");
  const isCustomErrors = Object.keys(errors)?.length > 0;
  const handleTooltipClose = () => {
    setIsTooltipOpen("");
  };
  const handleTooltipOpen = (id: "price" | "duration") => {
    setIsTooltipOpen(id);
  };
  const [findUserByPhone, { data: findUserData }] = useLazyQuery(
    FIND_FIRST_USER_BY_PHONE
  );

  const artistsResult = useQuery(GET_ARTISTS, {
    variables: {
      where: {
        user: {
          is: {
            joined_company_id: { equals: permissions?.companyId }
          }
        },
        is_active: {
          equals: true
        }
      }
    },
    fetchPolicy: "no-cache"
  });
  const data = artistsResult?.data ?? artistsResult?.previousData;

  const locationsResult = useQuery(GET_LOCATIONS, {
    variables: {
      where: {
        company_id: { equals: permissions?.companyId }
      }
    },
    fetchPolicy: "no-cache"
  });
  const locationsData: LocationResult[] =
    (locationsResult?.data ?? locationsResult?.previousData)?.locations ?? [];

  const createValidationSchema = () => {
    const baseSchema = {
      date: yup.date().nullable().required("Date required."),
      start: yup.date().nullable().required("Start time required."),
      location: yup.object({
        id: yup.string().nullable().required("Location selection required.")
      }),
      artist: yup.object({
        id: yup.string().nullable().required("Artist selection required.")
      })
    };

    const userSchema = {
      new: {
        first_name: yup.string().nullable().required("User name required."),
        last_name: yup.string().nullable().required("User last name required."),
        phone_number: yup
          .string()
          .test("phone", "Invalid phone number", (value) => {
            return isPhoneValid(value);
          })
      },
      search: {
        // phone_number_search: yup
        //   .string()
        //   .test("phone", "Invalid phone number", (value) => {
        //     return isPhoneValid(value);
        //   })
      },
      customers: {
        user_id: yup
          .string()
          .nullable()
          .required("Customer selection required.")
      }
    };
    return yup
      .object()
      .shape({
        ...baseSchema,
        ...userSchema[userSelectMode as keyof typeof userSchema]
      })
      .required();
  };

  const submit = async (submitData: any) => {
    let customErrors: any = {};
    if (userSelectMode === "search" && !findUserData?.findFirstUser?.id) {
      customErrors.foundUser = translate(
        "appointment.no_existing_customer_found"
      );
    }
    if (cart?.services?.length === 0) {
      customErrors.services = translate("appointment.at_least_one_service");
    }
    if (Object.keys(customErrors)?.length > 0) {
      setErrors(customErrors);
      return;
    }
    try {
      setIsSaving(true);
      const {
        date: submitDate,
        start,
        location,
        artist,
        notes,
        user_id,
        first_name,
        last_name,
        phone_number
      } = submitData;
      if (userSelectMode === "new") {
        const existingUser = await checkExistingUserForNewCustomer(
          phone_number
        );
        if (existingUser) {
          setIsSaving(false);
          selectExistingUser(phone_number, existingUser);
          return;
        }
      }
      const locationTimezone =
        locationsData.find((loc) => loc?.id === location?.id)?.timezone ??
        "PST8PDT";

      const mdate = dayjs.tz(submitDate as string, locationTimezone);

      const padZero = (num: any) => num.toString().padStart(2, "0");

      const year = mdate.year();
      const month = padZero(mdate.month() + 1);
      const day = padZero(dayjs(submitDate as string).date());
      const startHour = padZero(start.getHours());
      const startMinute = padZero(start.getMinutes());

      const startDateTimeString = `${year}-${month}-${day} ${startHour}:${startMinute}`;

      const start_time = dayjs.tz(
        startDateTimeString,
        "YYYY-MM-DD HH:mm",
        locationTimezone
      );

      const sortedServices = cart.services?.slice().sort((a: any, b: any) => {
        const isAPedicure = a.service.category === "PEDICURE";
        const isBPedicure = b.service.category === "PEDICURE";

        if (isAPedicure && !isBPedicure) return -1;
        if (!isAPedicure && isBPedicure) return 1;
        return 0;
      });

      const requestBody: RequestBody = {
        start_time: start_time.format(),
        location_id: location?.id,
        services: sortedServices?.map((service: any) => ({
          artist_id: artist?.id,
          service_id: service?.service?.id,
          addons_ids: service?.addOns
        })),
        notes: notes,
        ...(userSelectMode === "customers" && {
          customer_id: user_id
        }),
        ...(userSelectMode === "search" && {
          customer_id: findUserData?.findFirstUser?.id
        }),
        ...(userSelectMode === "new" && {
          phone_number: phone_number,
          first_name: first_name,
          last_name: last_name
        })
      };
      await api.post(`/v1/book-appointment/click-to-book`, requestBody);
      setIsSaving(false);
      closeCreateDialogAndRefresh?.();
    } catch (error) {
      setIsSaving(false);
      setErrors({ networkError: error?.response?.data?.message });
    }
  };

  const disabled =
    permissions.role.role !== "OWNER" &&
    permissions.role.role !== "MANAGER" &&
    !permissions.isSuperAdmin;

  const checkUser = (phoneNumber: string) => {
    return findUserByPhone({
      variables: {
        where: {
          OR: [
            {
              phone_number: {
                equals: phoneNumber
              }
            },
            {
              libphonenumber: {
                equals: getRawPhoneNumber(phoneNumber)
              }
            }
          ]
        }
      },
      fetchPolicy: "no-cache"
    });
  };

  const selectExistingUser = (phoneNumber: string, user: any) => {
    setExistingUserPhone({ phone: phoneNumber });
    setUserSelectMode("search");
    setIsCustomerSearchTriggered(true);
    notify(
      translate("appointment.existing_customer_selected", {
        name: `${user?.first_name} ${user?.last_name}`
      }),
      { type: "success" }
    );
  };

  const checkExistingUserForNewCustomer = async (phoneNumber: string) => {
    if (!isPhoneValid(phoneNumber)) return null;
    const result = await checkUser(phoneNumber);
    return result?.data?.findFirstUser?.id ? result.data.findFirstUser : null;
  };

  const totals = cart?.services?.reduce(
    (accumulator: any, booking: any) => {
      let bookingTotalPrice = booking?.service?.price;
      let bookingTotalDuration = booking?.service?.duration;

      if (booking?.addOns && booking?.service?.add_ons) {
        booking?.addOns?.forEach((addOnId: any) => {
          const addOn = booking?.service?.add_ons?.find(
            (a: any) => a?.core_service?.id === addOnId
          );

          if (addOn && addOn?.core_service) {
            bookingTotalPrice += addOn?.core_service?.price || 0;
            bookingTotalDuration += addOn?.core_service?.duration || 0;
          }
        });
      }

      accumulator.totalPrice += bookingTotalPrice;
      accumulator.totalDuration += bookingTotalDuration;

      return accumulator;
    },
    { totalPrice: 0, totalDuration: 0 }
  );

  function formatDuration(minutes: number): string {
    if (minutes < 60) {
      return `${minutes}m`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;

      if (remainingMinutes === 0) {
        return `${hours}h`;
      } else {
        return `${hours}h and ${remainingMinutes}m`;
      }
    }
  }

  return (
    <Dialog
      key={artist?.id}
      open={createDialogOpen === artist?.id}
      onClose={(_, reason: any) => {
        if (reason === "backdropClick") {
          return;
        }
        closeCreateDialog();
      }}
      sx={{
        "& .MuiDialogContent-root": {
          padding: "0px"
        },
        "& .MuiDialog-paper": {
          borderRadius: "8px",
          backgroundColor: "#fff",
          maxWidth: "480px",
          width: "100%",
          "@media screen and (max-width:926px)": {
            maxWidth: "480px"
          }
        }
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          padding: "4px 16px 0px 16px"
        }}
      >
        <div className="w-full">
          <div className="w-full flex justify-between">
            <div className="w-full flex justify-center items-center -mr-[42px] text-[14px] font-['Inter'] font-[600] uppercase">
              {translate("appointment.create_pointment")}
            </div>
            <IconButton onClick={closeCreateDialog}>
              <CloseIcon />
            </IconButton>
          </div>
          <div className="w-full flex justify-center gap-3 mb-3">
            <CustomButton
              variant="primary"
              type="button"
              text={translate("appointment.create_pointment")}
            />
            <CustomButton
              variant="secondary"
              type="button"
              text="Block Time"
              onClick={() => setBlockTimeDialogOpen(true)}
            />
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            backgroundColor: "#fff",
            "& .RaToolbar-desktopToolbar": {
              backgroundColor: "#fff",
              minHeight: "40px",
              padding: "0px 16px 16px 16px"
            },
            "& .RaToolbar-mobileToolbar": {
              position: "relative !important",
              backgroundColor: "#fff",
              minHeight: "40px",
              padding: "0px 16px 16px 16px !important"
              // zIndex: 90
            },
            "& .RaCreate-noActions": {
              marginTop: 0
            }
          }}
        >
          <SimpleForm
            onSubmit={submit}
            // validate={validateDates}
            resolver={yupResolver(createValidationSchema())}
            className="create-form"
            id="save-create-appointment"
            sx={{
              // backgroundColor: "#fff",
              padding: "0px 16px !important",
              "& .MuiFilledInput-root": {
                borderRadius: "8px",
                border: "1px solid rgba(48, 47, 46, 0.06)"
              },
              "& .MuiFilledInput-input": {
                height: "24px",
                fontSize: "16px",
                borderRadius: "8px",
                fontFamily: "Inter",
                border: "none"
              },
              "& .MuiFilledInput-input:focus": {
                boxShadow: "none",
                borderRadius: "8px !important"
              },
              "& .MuiFilledInput-root:before": {
                border: "none !important"
              },
              "& .MuiFilledInput-root:after": {
                border: "1px solid #b8722a !important",
                top: "0px",
                borderRadius: "8px",
                transition: "none"
              },
              "& .MuiFilledInput-root.Mui-error:after": {
                border: "1px solid #EF5B1B !important"
              },
              "& .MuiFilledInput-root.Mui-disabled": {
                backgroundColor: "rgba(48, 47, 46, 0.06)"
              },
              "& .MuiTextField-root": {
                marginTop: "3px",
                marginBottom: "3px"
              },
              "& .MuiInputLabel-root": {
                fontSize: "16px",
                color: "rgba(48, 47, 46, 0.6)"
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#b8722a"
              },
              "& .MuiFormHelperText-root": {
                fontSize: "12px",
                marginTop: "2px",
                marginBottom: "2px",
                lineHeight: "14px"
              }
            }}
            toolbar={false}
          >
            <BookFromArtistCalendarFormHelper
              resetUserInputs={resetUserInputs}
              existingUserPhone={existingUserPhone}
            />
            <div className="w-full">
              <h5 className="mb-2">
                {translate("appointment.select_customer")}:
              </h5>
              <div className="w-full flex gap-1 justify-between mb-3">
                <CustomButton
                  variant={
                    userSelectMode === "customers" ? "primary" : "secondary"
                  }
                  // disabled={userSelectMode === "customers" ? true : false}
                  type="button"
                  text={translate("appointment.customers")}
                  onClick={() => {
                    if (
                      findUserData?.findFirstUser?.id &&
                      userSelectMode === "search"
                    ) {
                      setIsCloseIconShaking(true);
                      setTimeout(() => {
                        setIsCloseIconShaking(false);
                      }, 500);
                      return;
                    }
                    setUserSelectMode("customers");
                    setResetUserInputs((prev) => !prev);
                  }}
                />
                <CustomButton
                  variant={
                    userSelectMode === "search" ? "primary" : "secondary"
                  }
                  // disabled={userSelectMode === "search" ? true : false}
                  type="button"
                  text={translate("appointment.search_by_phone")}
                  onClick={() => {
                    setUserSelectMode("search");
                    if (!findUserData?.findFirstUser?.id) {
                      setResetUserInputs((prev) => !prev);
                    }
                  }}
                />
                <CustomButton
                  variant={userSelectMode === "new" ? "primary" : "secondary"}
                  // disabled={userSelectMode === "new" ? true : false}
                  type="button"
                  text={translate("appointment.new_customer")}
                  onClick={() => {
                    if (
                      findUserData?.findFirstUser?.id &&
                      userSelectMode === "search"
                    ) {
                      setIsCloseIconShaking(true);
                      setTimeout(() => {
                        setIsCloseIconShaking(false);
                      }, 500);
                      return;
                    }
                    setUserSelectMode("new");
                    setResetUserInputs((prev) => !prev);
                  }}
                />
              </div>
              {userSelectMode === "customers" && (
                <ReferenceInput
                  source="user_id"
                  reference="User"
                  filter={{
                    OR: [
                      {
                        booked_appointments: {
                          some: {
                            location_id: {
                              in: locationsData?.map((location) => location.id)
                            }
                          }
                        }
                      },
                      {
                        joined_company_id: permissions.companyId
                      }
                    ]
                  }}
                  enableGetChoices={({ q }) => {
                    return q?.trim()?.length > 2;
                  }}
                  perPage={10}
                >
                  <AutocompleteInput
                    optionText={(value) =>
                      `${value?.first_name} ${value?.last_name}`
                    }
                    shouldRenderSuggestions={(val: any) => {
                      return val?.trim()?.length > 2;
                    }}
                    label={translate("appointment.customers")}
                    fullWidth
                    noOptionsText={translate(
                      "appointment.search_to_find_customers"
                    )}
                  />
                </ReferenceInput>
              )}
              {userSelectMode === "search" && (
                <CheckUserByPhoneNumber
                  checkFunction={checkUser}
                  userId={findUserData?.findFirstUser?.id}
                  isCustomerSearchTriggered={isCustomerSearchTriggered}
                  setIsCustomerSearchTriggered={setIsCustomerSearchTriggered}
                />
              )}
              {findUserData?.findFirstUser?.id &&
                userSelectMode === "search" && (
                  <div
                    aria-hidden
                    className="flex w-min items-center gap-[8px] rounded-[20px] mb-3 transition-colors duration-200 ease-in-out"
                    style={{
                      backgroundColor: "rgba(184, 114, 42, 0.1)"
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: "rgba(238, 234, 228, 0.9)",
                        color: "rgba(48, 47, 46, 0.95)",
                        fontFamily: "Inter",
                        fontSize: "18px"
                      }}
                      alt={findUserData?.findFirstUser?.first_name || "avatar"}
                      src={findUserData?.findFirstUser?.photo_url || "monogram"}
                    />
                    <div
                      className="max-w-[150px] truncate whitespace-nowrap text-[16px] font-[400] font-['Inter']"
                      style={{
                        color: "#B8722A"
                      }}
                    >
                      {`${findUserData?.findFirstUser?.first_name} ${findUserData?.findFirstUser?.last_name}`}
                    </div>
                    <IconButton
                      style={{ width: "40px", height: "40px" }}
                      className={isCloseIconShaking ? "shakeAnimation" : ""}
                      onClick={() => {
                        checkUser("");
                        setIsCustomerSearchTriggered(false);
                      }}
                    >
                      <CloseIcon
                        style={{
                          color: "#EF5B1B",
                          width: "22px",
                          height: "22px"
                        }}
                      />
                    </IconButton>
                  </div>
                )}
              {userSelectMode === "new" && (
                <div className="w-full">
                  <PhoneInput
                    source="phone_number"
                    fullWidth
                    checkSubmitted
                    onBlur={async (phoneNumber) => {
                      const existingUser =
                        await checkExistingUserForNewCustomer(phoneNumber);
                      if (existingUser && userSelectModeRef.current === "new") {
                        selectExistingUser(phoneNumber, existingUser);
                      }
                    }}
                  />
                  <TextInput
                    source="first_name"
                    label={"* " + translate("users.first_name")}
                    fullWidth
                  />
                  <TextInput
                    source="last_name"
                    label={"* " + translate("users.last_name")}
                    fullWidth
                  />
                </div>
              )}
              <div className="w-full h-[2px] mt-2 mb-4 bg-[rgba(0,0,0,0.07)]" />
              <h5 className="mb-2">
                {translate("appointment.general_details")}:
              </h5>
              <DatePickerInput
                source="date"
                disabled={disabled}
                label={translate("availability.date")}
                defaultValue={
                  selectedStart
                    ? new Date(selectedStart)
                    : location.state?.range?.start_time
                }
                validate={[required()]}
                fullWidth
              />
              <TimePickerInput
                source="start"
                disabled={disabled}
                label={translate("availability.start")}
                defaultValue={
                  selectedStart && !!selectedEnd
                    ? new Date(selectedStart)
                    : location.state?.range?.start_time
                }
                // inputProps={{
                //   placeholder: "HH:MM",
                //   pattern: "^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$"
                // }}
                validate={[required()]}
                fullWidth
              />
              {/* <TimeInput
            source="end"
            disabled={disabled}
            label={translate("availability.end")}
            defaultValue={
              selectedEnd
                ? new Date(selectedEnd)
                : location.state?.range?.end_time
            }
            validate={[required()]}
            fullWidth
          /> */}
              {locationsData && locationsData.length > 0 && (
                <ReferenceInput
                  source="location.id"
                  reference="Location"
                  filter={{ company_id: permissions.companyId }}
                >
                  <AutocompleteInput
                    optionText={(value) => value.nickname || value.name}
                    disabled={disabled}
                    label={translate("availability.location")}
                    fullWidth
                    onChange={() => setCart(emptyBookAppointmentFull)}
                    defaultValue={
                      filteredLocationId === "all"
                        ? undefined
                        : filteredLocationId
                    }
                    validate={[required()]}
                  />
                </ReferenceInput>
              )}
              {data?.artists && data?.artists?.length > 0 && (
                <AutocompleteInput
                  source="artist.id"
                  choices={
                    data?.artists?.map((item: any) => ({
                      id: item?.id,
                      name: `${item?.user?.first_name} ${item?.user?.last_name}`
                    })) || []
                  }
                  optionText={(value) => value?.name}
                  disabled={disabled}
                  defaultValue={artist?.id}
                  label={translate("appointmentservices.artist")}
                  fullWidth
                  validate={[required()]}
                />
              )}
              <TextInput
                source="notes"
                label={translate("appointment.note")}
                fullWidth
                multiline
              />
            </div>
            <div className="w-full h-[2px] mt-2 mb-4 bg-[rgba(0,0,0,0.07)]" />
            <div className="w-full">
              <h5 className="mb-2">
                {translate("appointment.select_services")}:
              </h5>
              <AddServicesAndAddOnsFromArtistCalendar
                source="services"
                locationsData={locationsData}
                cart={cart}
                setCart={setCart}
              />
            </div>
          </SimpleForm>
          {isCustomErrors && (
            <Dialog
              open={isCustomErrors}
              onClose={() => setErrors({})}
              sx={{
                "& .MuiDialogContent-root": {
                  padding: "0px"
                },
                "& .MuiDialog-paper": {
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  maxWidth: "520px",
                  width: "100%"
                }
              }}
            >
              <DialogTitle
                sx={{ display: "flex", padding: "4px 16px 0px 16px" }}
              >
                <div className="w-full flex justify-center items-center ml-[-12px] text-[14px] font-['Inter'] font-[600]" />
                <IconButton onClick={() => setErrors({})}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <div className="w-full flex flex-col justify-center items-center px-2">
                  <FailedOrderIcon />

                  <p className="text-[24px] font-['OptimaLTStd'] font-[500] text-mono-primaryTextColor mt-4">
                    {errors?.networkError
                      ? "Something went wrong"
                      : translate(
                          "appointment.you_need_to_fill_required_fields"
                        )}
                  </p>
                  <div className="my-6">
                    {Object.values(errors).map((error: any, index: number) => (
                      <p
                        key={index}
                        className="text-[18px] font-['OptimaLTStd'] font-[500] text-mono-primaryTextColor mt-1"
                      >
                        {errors?.networkError ? "" : index + 1 + "."} {error}
                      </p>
                    ))}
                  </div>
                </div>
              </DialogContent>
              <DialogActions>
                <div className="w-full flex justify-center mb-4">
                  <CustomButton
                    variant="primary"
                    text={translate("appointment.got_it")}
                    onClick={() => setErrors({})}
                  />
                </div>
              </DialogActions>
            </Dialog>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ paddingX: "16px" }}>
        <div className="w-full flex justify-between items-center">
          <ClickAwayListener onClickAway={handleTooltipClose}>
            <div className="">
              <div className="flex items-center gap-2">
                <div className="text-[12px] text-mono-secondaryTextColor font-['Inter'] font-[400]">
                  {translate("appointment.total_price")}:
                </div>{" "}
                <div className="text-[16px] text-mono-primaryTextColor font-['Inter'] font-[400]">
                  ${totals?.totalPrice}
                </div>
                {totals?.totalPrice > 0 && (
                  <div>
                    <Tooltip
                      id="tooltip-total-price"
                      onClose={handleTooltipClose}
                      open={isTooltipOpen === "price"}
                      placement="top"
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                      title={translate("appointment.fees_not_included")}
                      // @ts-ignore
                      // slotProps={{
                      //   popper: {
                      //     disablePortal: true
                      //   }
                      // }}
                    >
                      <Info
                        style={{ color: "#B8722A", cursor: "pointer" }}
                        onClick={() => handleTooltipOpen("price")}
                      />
                    </Tooltip>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className="text-[12px] text-mono-secondaryTextColor font-['Inter'] font-[400]">
                  {translate("appointment.total_duration")}:
                </div>{" "}
                <div className="text-[16px] text-mono-primaryTextColor font-['Inter'] font-[400]">
                  {totals?.totalDuration > 0 && "~"}{" "}
                  {formatDuration(totals?.totalDuration)}
                </div>
                {totals?.totalDuration > 0 && (
                  <div>
                    <Tooltip
                      id="tooltip-total-duration"
                      onClose={handleTooltipClose}
                      open={isTooltipOpen === "duration"}
                      placement="top"
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                      title={translate(
                        "appointment.clean_up_time_not_included"
                      )}
                    >
                      <Info
                        style={{ color: "#B8722A", cursor: "pointer" }}
                        onClick={() => handleTooltipOpen("duration")}
                      />
                    </Tooltip>
                  </div>
                )}
              </div>
            </div>
          </ClickAwayListener>
          <CustomButton
            type="submit"
            text={translate("common.save")}
            form="save-create-appointment"
            submitting={isSaving}
            disabled={isSaving || disabled}
          />
        </div>
      </DialogActions>
    </Dialog>
  );
}
